import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NewOrderPage = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [formData, setFormData] = useState({
    reception_location: 'TIENDA SIGAL EXTINTORES',
    reception_date: new Date().toISOString().split('T')[0],
    reception_received_by_name: ''
  });
  const [newClient, setNewClient] = useState({
    name: '',
    phone: '',
    address: ''
  });
  const [extinguishers, setExtinguishers] = useState([{
    _uid: crypto.randomUUID(),
    agent_type: 'Polvo Químico Seco',
    capacity_value: '',
    capacity_unit: 'KG',
    manufacture_date: '',
    last_recharge_date: '',
    has_hydrostatic_test: 'N/A'
  }]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      logger.error('Error fetching clients:', error);
    }
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.strokeStyle = '#09090B';
    ctx.lineWidth = 2;
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleCreateClient = async () => {
    if (!newClient.name || !newClient.phone) {
      toast.error('Nombre y teléfono son requeridos');
      return;
    }

    try {
      const response = await axios.post(`${API}/clients`, newClient);
      setClients([...clients, response.data]);
      setSelectedClient(response.data.id);
      setShowNewClientForm(false);
      setNewClient({ name: '', phone: '', address: '' });
      toast.success('Cliente creado exitosamente');
    } catch (error) {
      logger.error('Error creating client:', error);
      toast.error('Error al crear cliente');
    }
  };

  const addExtinguisher = () => {
    setExtinguishers([...extinguishers, {
      _uid: crypto.randomUUID(),
      agent_type: 'Polvo Químico Seco',
      capacity_value: '',
      capacity_unit: 'KG',
      manufacture_date: '',
      last_recharge_date: '',
      has_hydrostatic_test: 'N/A'
    }]);
  };

  const removeExtinguisher = (index) => {
    if (extinguishers.length > 1) {
      setExtinguishers(extinguishers.filter((_, i) => i !== index));
    }
  };

  const updateExtinguisher = (index, field, value) => {
    const updated = [...extinguishers];
    updated[index][field] = value;
    setExtinguishers(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedClient) {
      toast.error('Selecciona un cliente');
      return;
    }

    if (!formData.reception_received_by_name) {
      toast.error('El nombre de quien entrega es requerido');
      return;
    }

    const invalidExt = extinguishers.find(ext => !ext.agent_type || !ext.capacity_value);
    if (invalidExt) {
      toast.error('Completa todos los campos de los extintores (Tipo de Agente, Capacidad)');
      return;
    }

    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');

    setSubmitting(true);
    try {
      const orderResponse = await axios.post(`${API}/orders`, {
        client_id: selectedClient,
        reception_date: new Date(formData.reception_date).toISOString(),
        reception_location: formData.reception_location,
        reception_received_by_name: formData.reception_received_by_name,
        reception_signature: signatureData
      });

      const orderId = orderResponse.data.id;

      for (const ext of extinguishers) {
        await axios.post(`${API}/extinguishers`, {
          order_id: orderId,
          agent_type: ext.agent_type,
          capacity_value: ext.capacity_value,
          capacity_unit: ext.capacity_unit,
          manufacture_date: ext.manufacture_date || null,
          last_recharge_date: ext.last_recharge_date || null,
          has_hydrostatic_test: ext.has_hydrostatic_test
        });
      }

      toast.success('Orden creada exitosamente');
      navigate(`/orders/${orderId}`);
    } catch (error) {
      logger.error('Error creating order:', error);
      toast.error('Error al crear la orden');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-8">
        <div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">Nueva Orden</h1>
          <p className="text-base text-muted-foreground mt-2">Registra una nueva orden de servicio</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="new-order-form">
          <div className="p-8 border border-border bg-background space-y-6">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">Información del Cliente</h2>

            {!showNewClientForm ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">Cliente</Label>
                  <select
                    value={selectedClient || ''}
                    onChange={(e) => setSelectedClient(e.target.value)}
                    data-testid="client-select"
                    className="w-full px-4 py-2 border border-border bg-background text-foreground"
                    required
                  >
                    <option value="">Seleccionar cliente...</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name} - {client.phone}
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowNewClientForm(true)}
                  data-testid="show-new-client-form"
                  className="hover:bg-primary hover:text-primary-foreground"
                >
                  <Plus size={16} className="mr-2" />
                  Crear Nuevo Cliente
                </Button>
              </div>
            ) : (
              <div className="space-y-4 p-6 bg-secondary">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Nuevo Cliente</h3>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowNewClientForm(false)}
                  >
                    <X size={16} />
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">Nombre *</Label>
                  <Input
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    data-testid="new-client-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">Teléfono *</Label>
                  <Input
                    value={newClient.phone}
                    onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                    data-testid="new-client-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">Dirección</Label>
                  <Input
                    value={newClient.address}
                    onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                    data-testid="new-client-address"
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleCreateClient}
                  data-testid="create-client-button"
                  className="bg-primary hover:bg-primary/90"
                >
                  Guardar Cliente
                </Button>
              </div>
            )}
          </div>

          <div className="p-8 border border-border bg-background space-y-6">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">Información de la Orden</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="reception_location" className="text-xs font-bold uppercase tracking-[0.2em]">Lugar de Recepción *</Label>
                <select
                  id="reception_location"
                  value={formData.reception_location}
                  onChange={(e) => setFormData({ ...formData, reception_location: e.target.value })}
                  data-testid="reception-location-select"
                  className="w-full px-4 py-2 border border-border bg-background text-foreground"
                  required
                >
                  <option value="TIENDA SIGAL EXTINTORES">TIENDA SIGAL EXTINTORES</option>
                  <option value="DOMICILIO DE CLIENTE">DOMICILIO DE CLIENTE</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reception_date" className="text-xs font-bold uppercase tracking-[0.2em]">Fecha de Recepción *</Label>
                <Input
                  id="reception_date"
                  type="date"
                  value={formData.reception_date}
                  onChange={(e) => setFormData({ ...formData, reception_date: e.target.value })}
                  data-testid="reception-date-input"
                  required
                />
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="reception_received_by_name" className="text-xs font-bold uppercase tracking-[0.2em]">Nombre de quien Entrega los Extintores *</Label>
                <Input
                  id="reception_received_by_name"
                  value={formData.reception_received_by_name}
                  onChange={(e) => setFormData({ ...formData, reception_received_by_name: e.target.value })}
                  data-testid="reception-received-by-name-input"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em]">
                Firma de quien Entrega *
              </Label>
              <div className="border-2 border-border bg-white">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={150}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  className="w-full cursor-crosshair"
                  data-testid="reception-signature-canvas"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clearSignature}
                data-testid="clear-reception-signature-button"
                className="hover:bg-secondary"
              >
                Limpiar Firma
              </Button>
            </div>
          </div>

          <div className="p-8 border border-border bg-background space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">Extintores</h2>
              <Button
                type="button"
                variant="outline"
                onClick={addExtinguisher}
                data-testid="add-extinguisher-button"
                className="hover:bg-primary hover:text-primary-foreground"
              >
                <Plus size={16} className="mr-2" />
                Agregar Extintor
              </Button>
            </div>

            {extinguishers.map((ext, index) => (
              <div key={ext._uid} className="p-6 bg-secondary space-y-4" data-testid={`extinguisher-${index}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Extintor #{index + 1}</h3>
                  {extinguishers.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeExtinguisher(index)}
                      data-testid={`remove-extinguisher-${index}`}
                    >
                      <X size={16} />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-[0.2em]">Tipo de Agente *</Label>
                    <select
                      value={ext.agent_type}
                      onChange={(e) => updateExtinguisher(index, 'agent_type', e.target.value)}
                      data-testid={`ext-agent-${index}`}
                      className="w-full px-4 py-2 border border-border bg-background text-foreground"
                      required
                    >
                      <option value="Polvo Químico Seco">Polvo Químico Seco</option>
                      <option value="Dióxido de Carbono">Dióxido de Carbono</option>
                      <option value="Acetato de Potasio">Acetato de Potasio</option>
                      <option value="Agua a Presión">Agua a Presión</option>
                      <option value="Agua Desionizada">Agua Desionizada</option>
                      <option value="Sal Metálica con Aditivos">Sal Metálica con Aditivos</option>
                      <option value="Encapsulador de Micelas F500">Encapsulador de Micelas F500</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-[0.2em]">Capacidad *</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="number"
                        step="0.1"
                        value={ext.capacity_value}
                        onChange={(e) => updateExtinguisher(index, 'capacity_value', e.target.value)}
                        placeholder="Ej: 6, 9, 12"
                        data-testid={`ext-capacity-value-${index}`}
                        required
                      />
                      <select
                        value={ext.capacity_unit}
                        onChange={(e) => updateExtinguisher(index, 'capacity_unit', e.target.value)}
                        data-testid={`ext-capacity-unit-${index}`}
                        className="px-4 py-2 border border-border bg-background text-foreground"
                        required
                      >
                        <option value="KG">KG</option>
                        <option value="LBS">LBS</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-[0.2em]">Fecha de Fabricación</Label>
                    <Input
                      type="date"
                      value={ext.manufacture_date}
                      onChange={(e) => updateExtinguisher(index, 'manufacture_date', e.target.value)}
                      data-testid={`ext-manufacture-${index}`}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-[0.2em]">Última Recarga</Label>
                    <Input
                      type="date"
                      value={ext.last_recharge_date}
                      onChange={(e) => updateExtinguisher(index, 'last_recharge_date', e.target.value)}
                      data-testid={`ext-recharge-${index}`}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-[0.2em]">¿Cuenta con Prueba Hidrostática?</Label>
                    <select
                      value={ext.has_hydrostatic_test}
                      onChange={(e) => updateExtinguisher(index, 'has_hydrostatic_test', e.target.value)}
                      data-testid={`ext-hydrostatic-${index}`}
                      className="w-full px-4 py-2 border border-border bg-background text-foreground"
                    >
                      <option value="SI">SI</option>
                      <option value="NO">NO</option>
                      <option value="N/A">N/A</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/orders')}
              data-testid="cancel-button"
              className="hover:bg-secondary"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="submit-order-button"
              className="bg-primary hover:bg-primary/90"
            >
              {submitting ? 'Creando...' : 'Crear Orden'}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};
